import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { PaymentOption } from '@merchant-portal/app/models/disbursement/disbursement.interface';
import { TopUpInstruction } from '@merchant-portal/app/models/wallet/merchant-history/top-up-instruction.interface';
import { environment } from '@merchant-portal/environments/environment';
import { Observable, catchError, map, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TopUpInstructionService {
  private http = inject(HttpClient);
  private url = environment.backend_portal_url;

  getTopUpInstructions(
    paymentMethodId: string,
    selectedBank: PaymentOption,
    context: 'wallet' | 'disbursement' = 'disbursement'
  ): Observable<TopUpInstruction> {
    // Determine the correct API endpoint based on context
    const endpoint =
      context === 'wallet'
        ? `${this.url}api/v1/wallets/merchants/top-up`
        : `${this.url}api/v1/disbursements/top-up`;
    // Post to the API regardless of payment method type
    return this.http
      .post<any>(endpoint, {
        paymentMethodId,
      })
      .pipe(
        map(response => {
          let accountNumber = '';
          if (selectedBank.type === 'BANK_TRANSFER' && selectedBank.description) {
            const descriptionLines = selectedBank.description.split('\\n');
            if (descriptionLines.length > 1) {
              accountNumber = descriptionLines[1];
            }
          } else if (response.data && response.data.referenceNumber) {
            accountNumber = response.data.referenceNumber;
          }

          if (response.data && response.data.instructions) {
            return {
              bankName: selectedBank.bankName || `Bank ${selectedBank.acquirer.toUpperCase()}`,
              bankCode: selectedBank.acquirer,
              bankLogo: selectedBank.logo,
              virtualAccountNumber: accountNumber,
              recipientName: 'PT. Harsya Remitlindo',
              instructions: this.parseHtmlInstructions(response.data.instructions),
              rawHtmlInstructions: response.data.instructions, // Store the raw HTML in case needed
            };
          }

          return {
            bankName: selectedBank.bankName || `Bank ${selectedBank.acquirer.toUpperCase()}`,
            bankCode: selectedBank.acquirer,
            bankLogo: selectedBank.logo,
            virtualAccountNumber: accountNumber,
            recipientName: 'PT. Harsya Remitlindo',
            instructions: [
              {
                title: 'Payment Instructions',
                steps: [
                  'Please follow the instructions provided by your bank to complete the payment.',
                  selectedBank.type === 'BANK_TRANSFER'
                    ? `Transfer to account number: ${accountNumber}`
                    : `Pay using the virtual account number: ${accountNumber}`,
                  'Contact customer support if you need assistance.',
                ],
              },
            ],
          };
        }),
        catchError(error => {
          console.error('Error fetching top-up instructions:', error);
          return of({
            bankName: selectedBank.bankName || `Bank ${selectedBank.acquirer.toUpperCase()}`,
            bankCode: selectedBank.acquirer,
            bankLogo: selectedBank.logo,
            virtualAccountNumber: '',
            recipientName: 'PT. Harsya Remitlindo',
            instructions: [
              {
                title: 'Payment Instructions',
                steps: [
                  'Unable to load payment instructions at this time.',
                  'Please try again later or contact customer support.',
                ],
              },
            ],
          });
        })
      );
  }

  /**
   * Parse HTML instructions into structured format for the UI
   * The expected HTML format is:
   * <div>
   *   <h3>Title 1</h3>
   *   <ol>
   *     <li>Step 1</li>
   *     <li>Step 2</li>
   *   </ol>
   *   <h3>Title 2</h3>
   *   <ol>
   *     <li>Step 1</li>
   *     <li>Step 2</li>
   *   </ol>
   * </div>
   */
  private parseHtmlInstructions(htmlInstructions: string): { title: string; steps: string[] }[] {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlInstructions, 'text/html');
      const instructionSections: { title: string; steps: string[] }[] = [];

      // Get all h3 elements as section titles
      const titles = doc.querySelectorAll('h3');

      titles.forEach(titleElement => {
        const title = titleElement.textContent || '';
        const stepsElement = titleElement.nextElementSibling;
        const steps: string[] = [];

        if (stepsElement && stepsElement.tagName.toLowerCase() === 'ol') {
          const stepItems = stepsElement.querySelectorAll('li');
          stepItems.forEach(item => {
            steps.push(item.textContent || '');
          });
        }

        if (title && steps.length > 0) {
          instructionSections.push({ title, steps });
        }
      });

      return instructionSections.length > 0
        ? instructionSections
        : this.createGenericInstructionSection(htmlInstructions);
    } catch (error) {
      console.error('Error parsing HTML instructions:', error);
      return this.createGenericInstructionSection(htmlInstructions);
    }
  }

  private createGenericInstructionSection(
    htmlContent: string
  ): { title: string; steps: string[] }[] {
    return [
      {
        title: 'Payment Instructions',
        steps: [
          'Please follow the instructions below to complete your payment:',
          'If you have any questions, please contact customer support.',
        ],
      },
    ];
  }
}
